// Component: Modal.jsx
// Purpose: DS dialog — portal + dimmed backdrop + centered panel. Full a11y:
//   role=dialog/aria-modal, focus-trap (Tab cycles within), focus first control
//   on open + restore on close, ESC + backdrop-click close, body scroll-lock.
//   Sizes sm/md/lg, header/body/footer slots. Styling in ds-components.css.
//   `onClose` need not be memoized; the open lifecycle is keyed on `open` only (DS RULE in
//   components/ds/index.js).

import React, { useEffect, useLayoutEffect, useRef, useId } from "react";
import { createPortal } from "react-dom";
import { cx } from "./cx";

const SIZES = { sm: "sm", md: "", lg: "lg" };
const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function Modal({
  open,
  onClose,
  title,
  size = "md",
  children,
  footer,
  closeOnBackdrop = true,
  closeOnEsc = true,
  dismissible = true,
  className,
  ...props
}) {
  const panelRef = useRef(null);
  const lastFocused = useRef(null);
  const titleId = useId();

  //* Latest close props, read by the keydown handler at key time (DS RULE: components/ds/index.js).
  //  Written in the layout phase of every commit (before any passive effect or key event), never
  //  during render. When React >= 19.2: replace with `useEffectEvent` from "react".
  const latest = useRef({ onClose, dismissible, closeOnEsc });
  useLayoutEffect(() => {
    latest.current = { onClose, dismissible, closeOnEsc };
  });

  useEffect(() => {
    if (!open) return undefined;

    //--> Remember what had focus, lock scroll, focus into the dialog.
    lastFocused.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const panel = panelRef.current;
    const focusables = panel ? panel.querySelectorAll(FOCUSABLE) : [];
    (focusables[0] || panel)?.focus();

    const onKey = (e) => {
      if (e.key === "Escape" && latest.current.dismissible && latest.current.closeOnEsc) {
        e.stopPropagation();
        latest.current.onClose?.();
        return;
      }
      //! Trap Tab within the panel (wrap at both ends).
      if (e.key === "Tab" && panel) {
        const items = panel.querySelectorAll(FOCUSABLE);
        if (items.length === 0) {
          e.preventDefault();
          return;
        }
        const first = items[0];
        const last = items[items.length - 1];
        //! Focus fell out of the dialog (the focused control unmounted, e.g. a footer swap) → pull it
        //  back in. Only the TOPMOST open dialog acts: document keydown listeners fire in registration
        //  order (the lower dialog's first), and portals append to <body> in mount order, so the last
        //  [role=dialog][aria-modal=true] in the document is the one on top.
        const active = document.activeElement;
        if (!active || active === document.body) {
          const dialogs = document.querySelectorAll('[role="dialog"][aria-modal="true"]');
          if (dialogs[dialogs.length - 1] !== panel) return;
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
          return;
        }
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      //--> Restore focus to the trigger on close.
      if (lastFocused.current && typeof lastFocused.current.focus === "function") {
        lastFocused.current.focus();
      }
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="ds-modal-backdrop"
      onMouseDown={(e) => {
        //! Only a press that starts AND ends on the backdrop closes it.
        if (dismissible && closeOnBackdrop && e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title != null ? titleId : undefined}
        className={cx("ds-modal", SIZES[size], className)}
        tabIndex={-1}
        {...props}
      >
        {title != null && (
          <div className="ds-modal-header">
            <h2 id={titleId} className="ds-modal-title">
              {title}
            </h2>
            {dismissible && (
              <button
                type="button"
                className="ds-modal-close"
                aria-label="Close"
                onClick={() => onClose?.()}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        )}
        <div className="ds-modal-body">{children}</div>
        {footer != null && <div className="ds-modal-footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

export default Modal;
